import { apiFetch } from './client.js';

/**
 * Cliente de la auditoría de precios (solo staff Ecosoft).
 * Ver `apps/api/src/routes/staffPrecios.ts` para el porqué.
 */

export type OrigenPrecio = 'mercado' | 'costo_horario' | 'catalogo';
export type FiltroOrigen = 'fuera_catalogo' | 'todos' | OrigenPrecio;

export interface PrecioAuditado {
  project_id: string;
  obra: string;
  empresa: string | null;
  obra_moneda: string | null;
  /** País declarado por la obra. null = no lo declaró. */
  obra_pais: string | null;
  /** País con el que se BUSCÓ el precio (el resolver asume MX si no hay declarado). */
  pais_busqueda: string;
  /** Etiqueta para agrupar/filtrar: ISO alpha-2 en mayúsculas, o 'SIN'. */
  pais_etiqueta: string;
  quote_id: string;
  concept_id: string;
  concepto_clave: string | null;
  concepto: string;
  tipo: 'material' | 'equipo';
  descripcion: string | null;
  unidad: string | null;
  precio: string;
  fuente: OrigenPrecio;
  // --- Origen concreto: catálogo ---
  catalog_id: string | null;
  catalogo_nombre: string | null;
  catalogo_kind: 'ecostos' | 'tenant_upload' | 'referencia' | null;
  catalogo_pais: string | null;
  catalogo_moneda: string | null;
  clave_catalogo: string | null;
  /** Cómo lo llama el catálogo. Comparado con `descripcion` (cómo lo pidió la IA)
   *  es lo que revela los problemas de vocabulario. */
  descripcion_catalogo: string | null;
  unidad_catalogo: string | null;
  score_match: string | null;
  incluye_mo: boolean | null;
  // --- Origen concreto: mercado ---
  /** Comercios que respaldan el precio, tal como los guardó el renglón.
   *  `[]` = referencia sin comercio detrás. `null` = el renglón no guardó el dato. */
  proveedores: string[] | null;
  // Trazabilidad de moneda (migración 074)
  moneda_nativa: string | null;
  precio_nativo: string | null;
  tipo_cambio: string | null;
  tipo_cambio_fecha: string | null;
  tipo_cambio_fuente: string | null;
  tipo_cambio_viejo: boolean | null;
  moneda_verificada: boolean;
  /** Piso de cordura: el precio quedó por debajo de lo que el catálogo del cliente
   *  registra para su tipo y unidad, y no se consiguió una referencia mejor. El
   *  renglón conserva el número (ningún recurso queda sin precio) — esto es la
   *  señal de que hay que auditarlo. */
  precio_no_confiable: boolean;
  motivo_no_confiable: string | null;
  // Costo horario del equipo
  valor_adquisicion: string | null;
  va_nativo: string | null;
  va_moneda_nativa: string | null;
  va_tipo_cambio: string | null;
  modelo_costo: string | null;
  // Traza de la búsqueda
  presentacion: string | null;
  nota: string | null;
  generado_en: string | null;
  /** Cuándo se consultó el mercado para este recurso (del caché por país).
   *  null = no hay consulta guardada para ese recurso en ese país. */
  consulted_at: string | null;
}

export interface PreciosAuditoria {
  items: PrecioAuditado[];
  total: number;
  sinMonedaConfirmada: number;
  /** Cuántos quedaron por debajo del piso de precios del catálogo. */
  bajoElPiso: number;
  /** Cuántos renglones hay por país ('SIN' = obra sin país declarado). */
  paises: { pais: string; n: number }[];
  /** Cuántos renglones hay por origen del precio. */
  origenes: { fuente: OrigenPrecio; n: number }[];
  limit: number;
  offset: number;
}

/** Una fila del caché de mercado: el precio que hay guardado para un país. */
export interface PrecioMercadoPais {
  country: string;
  kind: string;
  unidad: string;
  precio_unitario: string;
  currency: string | null;
  native_currency: string | null;
  presentacion: string | null;
  nota: string | null;
  descripcion_original: string | null;
  descripcion_norm: string;
  consulted_at: string;
  proveedores: string[];
  /** false = es un recurso PARECIDO, no el mismo (coincidencia por trigrama). */
  exacto: boolean;
  parecido: string;
}

export interface RecursoPorPais {
  mercadoPorPais: PrecioMercadoPais[];
  otrasObras: PrecioAuditado[];
}

export async function getPreciosAuditoria(params: {
  q?: string;
  orden?: 'precio' | 'precio_asc' | 'fecha';
  soloSospechosos?: boolean;
  origen?: FiltroOrigen;
  pais?: string;
  limit?: number;
  offset?: number;
}): Promise<PreciosAuditoria> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.orden) qs.set('orden', params.orden);
  if (params.soloSospechosos) qs.set('soloSospechosos', 'true');
  if (params.origen) qs.set('origen', params.origen);
  if (params.pais) qs.set('pais', params.pais);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  return apiFetch<PreciosAuditoria>(`/api/v1/staff/precios?${qs.toString()}`);
}

/** El mismo recurso, país por país: qué precio hay guardado y de dónde salió. */
export async function getRecursoPorPais(params: {
  descripcion: string;
  tipo: 'material' | 'equipo';
}): Promise<RecursoPorPais> {
  const qs = new URLSearchParams({ descripcion: params.descripcion, tipo: params.tipo });
  return apiFetch<RecursoPorPais>(`/api/v1/staff/precios/recurso?${qs.toString()}`);
}

export interface TipoCambioAplicado {
  base_currency: string;
  quote_currency: string;
  rate: string;
  rate_date: string;
  source: string;
  pivot_currency: string | null;
  fetched_at: string;
}

export async function getTiposCambioAplicados(): Promise<{ items: TipoCambioAplicado[] }> {
  return apiFetch<{ items: TipoCambioAplicado[] }>('/api/v1/staff/precios/tipos-cambio');
}
